<template>
    <div class="leaflet-map-container">
        <ClientOnly>
            <UiMapLeafletMap
                v-if="mapActive"
                :blok="blok"
                :airport="airport"
                :modifier="modifier"
                :product-list="productList"
            />
        </ClientOnly>
        <div v-if="!mapActive && blok.openMapBtn && blok.mapPlaceholderImage?.filename" class="map-placeholder">
            <NuxtImg
                v-if="blok.mapPlaceholderImage?.filename"
                :src="blok.mapPlaceholderImage.filename"
                :title="blok.mapPlaceholderImage.title"
                :alt="blok.mapPlaceholderImage.alt"
                loading="lazy"
                provider="storyblok"
                format="webp"
                densities="x1"
            />
        </div>
        <div v-if="blok.openMapBtn" class="map-btn" :class="{ 'map-active': mapActive }">
            <button v-if="!mapActive" @click="openMap()">
                <NuxtIcon name="ri:map-2-line" />
                {{ te('view_on_map') ? t('view_on_map') : 'View on Map' }}
            </button>
            <button v-else @click="closeMap()">
                <NuxtIcon name="ri:close-line" class="close-icon" />
                <span>{{ te('close_map') ? t('close_map') : 'Close map' }}</span>
            </button>
        </div>
        <div style="display: none">
            <!-- 10 closest to airport products in hidden container for SEO -->
            <ProductCard
                v-for="product in sortedByDistanceProducts?.slice(0, 10)"
                :key="product.id || (product as IHotelProduct).hotel_id"
                :product-data="product"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { IApiProductMap } from '@nuxt-monorepo/ui/types/components/Map/ApiProductMap';
import type { IAirportEntity, IAirportProducts, IHotelProduct, IParkingProduct } from '@nuxt-monorepo/ui/types';
import { filterProducts } from '@nuxt-monorepo/ui/utils/apiFilter';
import ProductCard from '@nuxt-monorepo/ui/components/Map/ProductCard.vue';

const { t, te } = useI18n();

export interface Props {
    airport: IAirportEntity;
    products: IAirportProducts;
    modifier?: string;
    blok: IApiProductMap;
    sortingFunctions: {
        parking: (a: IParkingProduct | IHotelProduct, b: IParkingProduct | IHotelProduct) => number;
        hotels: (a: IHotelProduct | IParkingProduct, b: IHotelProduct | IParkingProduct) => number;
    };
}

const { blok, airport, products, sortingFunctions } = defineProps<Props>();

const mapActive = ref(!blok.openMapBtn);
const openMap = async () => {
    mapActive.value = true;
    await nextTick();
};

const closeMap = () => {
    mapActive.value = false;
};

const isIHotelProduct = (product: IHotelProduct | IParkingProduct): product is IHotelProduct => {
    return (product as IParkingProduct).geo?.latitude === undefined;
};
// Checks coordinates of product to be not too far from airport
const checkCoordinates = (product: IHotelProduct | IParkingProduct) => {
    const lat = Number(
        isIHotelProduct(product) ? (product.latitude ?? product.address.latitude) : product.geo?.latitude
    );
    const lon = Number(
        isIHotelProduct(product) ? (product.longitude ?? product.address.longitude) : product.geo?.longitude
    );
    if (!lat && !lon) return false;
    if (Math.abs(lat - +airport?.latitude) > 1.5 || Math.abs(lon - +airport?.longitude) > 1.5) return false;
    return true;
};
const productList = filterProducts(blok, products)?.filter(checkCoordinates);
const sortedByDistanceProducts = [...(productList ?? [])].sort(
    blok.product_type === 'Parking' ? sortingFunctions.parking : sortingFunctions.hotels
);
</script>
<style lang="postcss" scoped>
.leaflet-map-container {
    @apply relative w-full overflow-hidden z-0;

    :deep(.map-container) {
        @apply z-10;
    }

    &:not(:has(button.gm-fullscreen-control)) {
        .map-btn.map-active {
            @apply max-md:min-w-32 left-auto -translate-x-0 -translate-y-0 right-2.5 top-2.5;
            button {
                @apply max-sm:px-1 w-full;
            }
            svg {
                @apply max-sm:mr-0;
            }
            span {
                @apply max-sm:inline-block;
            }
        }
    }

    .map-placeholder {
        @apply flex justify-center;
        img {
            @apply h-[140px] w-[350px] md:w-[500px] object-cover rounded-lg;
        }
    }
    .map-btn {
        @apply max-md:w-[200px] absolute left-2/4 top-2/4 -translate-x-2/4 -translate-y-2/4 z-20;

        &.map-active {
            @apply max-sm:right-6 max-md:w-auto left-auto right-0 top-8;

            button {
                @apply text-tertiary bg-white border border-tertiary;
                @apply max-sm:text-base max-sm:flex max-sm:items-center max-sm:justify-center;
                &:hover {
                    @apply bg-tertiary text-white;
                }
                svg {
                    @apply max-sm:mr-0;
                }
                span {
                    @apply max-sm:hidden;
                    &.iconify {
                        @apply max-sm:inline-block;
                    }
                }
            }
        }

        button {
            @apply bg-tertiary text-white font-bold text-lg py-2 px-6 rounded flex justify-center items-center gap-x-1;
            &:hover {
                @apply bg-[#f8791e];
            }

            .close-icon {
                @apply h-5 w-5;
            }
        }
    }
    :deep {
        .leaflet-popup-content-wrapper {
            @apply p-0;
        }
        .leaflet-popup-content {
            @apply m-0;
        }
        .leaflet-popup-close-button {
            @apply bg-white rounded-tr;
        }
        .leaflet-map-icon {
            @apply !max-h-9;
        }
    }
}
</style>
